import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  IconAttention,
  BlacklistWarningItem,
  PermissionEnum,
  SidePanel,
  SidePanelType,
  ButtonPseudo,
  BlacklistDataContent,
  IconSelected,
  TransactionWarningActionEnum,
  IconBlock,
  ButtonSize,
  ButtonSecondary,
  IconSandWatch,
  IconLoader,
  IconChangeLoClock,
  Popup,
  Select,
  MatchStatusEnum,
  Textarea,
  BlacklistUserInfo,
} from '@ui/src';
import { NewApplicationStatus, OngoingStatus } from '../../hoc/customers/types';
import { OngoingMonitoringTypes } from './types';

import cn from './new-application.module.css';
import StepPartBlock from './corporate/parts/step-part-block';

const StepFive = ({
  ongoingMonitoring,
  status,
  currentEmployee,
  activePermissions,
  isLoading,
  isAllowed,
  applyError,
  applyWarningInProgress,
  applyOngoingWarning,
  batchApplyWarningInProgress,
  batchApplyOngoingWarning,
  ongoingCheckingStatus,
  restartOngoingCheckingAction,
  restartInProgress,
  onResetTransactionWarningAction,
  resetTransactionsWarningIsLoading,
  resetTransactionsWarningIsSuccess,
  batchApplyWarningIsSuccess,
  selectedWarningId,
  getApplicationWarningsAction,
  paginationInProgress,
  selectedWarningGroup,
  blacklistData,
  setBlacklistData,
  blacklistDataVisible,
  setBlacklistDataVisible,
  blacklistItem,
  setBlacklistDataItem,
  blacklistItemVisible,
  setBlacklistItemVisible,
  getOngoingWarningById,
  isWarningByIdLoading,
  isWarningByIdFetching,
}: OngoingMonitoringTypes) => {
  const [applyAllPopupVisible, setApplyAllPopupVisible] = useState(false);
  const [comment, setComment] = useState('');
  const [action, setAction] = useState('');
  const [matchStatus, setMatchStatus] = useState('');

  const ongoingWithoutActions = [];

  ongoingMonitoring?.forEach((item) => {
    item?.warnings?.edges?.forEach((warning) => {
      if (!warning.node.action) {
        ongoingWithoutActions.push(warning);
      }
    });
  });

  const onApplyActions = async () => {
    await batchApplyOngoingWarning({
      action,
      comment,
      matchStatus,
    });
  };

  useEffect(() => {
    if (batchApplyWarningIsSuccess) {
      setApplyAllPopupVisible(false);
      setComment('');
      setAction('');
      setMatchStatus('');
    }
  }, [batchApplyWarningIsSuccess]);

  let content;

  if (!isAllowed) {
    content = (
      <div className={cn.waitingStatus}>
        <IconAttention className={cn.redIcon} />
        You dont have access to this section
      </div>
    );
  } else if (isAllowed) {
    if (isLoading) {
      content = (
        <div className={cn.ongoingLoader}>
          <IconLoader />
        </div>
      );
    } else if (!isLoading) {
      if (status === NewApplicationStatus.INCOMPLETE) {
        content = (
          <div className={cn.waitingStatus}>
            Blacklist Checking will start with the “Waiting for review” status
          </div>
        );
      } else if (
        ongoingCheckingStatus?.status === OngoingStatus.FAIL ||
        !ongoingCheckingStatus
      ) {
        content = (
          <div className={cn.waitingStatus}>
            <IconBlock className={cn.blockIcon} />
            An error occurred while checking
            <ButtonSecondary
              onClick={restartOngoingCheckingAction}
              size={ButtonSize.SMALL}
              text={restartInProgress ? 'Loading...' : 'Try again'}
              className={cn.restartButton}
              disabled={restartInProgress}
            />
          </div>
        );
      } else if (
        ongoingCheckingStatus?.status === OngoingStatus.QUEUED ||
        ongoingCheckingStatus?.status === OngoingStatus.PROCESSING
      ) {
        content = (
          <div className={cn.waitingStatus}>
            <IconSandWatch className={cn.iconSandWatch} />
            Checking for sanctions lists...
          </div>
        );
      } else if (
        ongoingMonitoring?.length &&
        ongoingMonitoring[0]?.warnings?.edges?.length
      ) {
        content = (
          <div className={cn.ongoingWrapper}>
            {ongoingWithoutActions?.length > 0 && (
              <>
                <div className={cn.ongoingTop}>
                  <IconAttention className={cn.redIcon} />
                  Sanction list matching detected
                  {activePermissions?.includes(
                    PermissionEnum.AUDIT_LOG_LIST,
                  ) && (
                    <NavLink
                      to={`/administration/change-logs?entityType=WARNING_GROUP&entityId=${ongoingMonitoring[0].id}`}
                      className={cn.changeLogLink}
                      target="_blank"
                    >
                      <IconChangeLoClock />
                      Change Log
                    </NavLink>
                  )}
                </div>
                <div className={cn.ongoingActions}>
                  {(activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_AML_APPROVE_ACTION,
                  ) ||
                    activePermissions?.includes(
                      PermissionEnum.APPLICATIONS_CUSTOMERS_AML_REJECT_ACTION,
                    )) && (
                    <ButtonSecondary
                      size={ButtonSize.SMALL}
                      text="Apply to all matches"
                      className={`button-white ${cn.applyAll}`}
                      onClick={() => setApplyAllPopupVisible(true)}
                    />
                  )}
                </div>
              </>
            )}

            {ongoingMonitoring?.map((item) => (
              <div key={item.id}>
                {item?.warnings?.edges?.map((warning) => (
                  <BlacklistWarningItem
                    key={warning.node.id}
                    warning={warning.node}
                    employee={currentEmployee}
                    onApplyTransactionAction={(data) => {
                      applyOngoingWarning({
                        id: item.id,
                        warningId: data.warningId,
                        action: data.body.action,
                        comment: data.body.comment,
                        matchStatus: data.body.matchStatus,
                      });
                    }}
                    setBlacklistDataVisible={() => {
                      setBlacklistDataVisible(true);
                    }}
                    setBlacklistItemVisible={() => {
                      setBlacklistItemVisible(true);
                    }}
                    applyTransactionsWarningIsLoading={applyWarningInProgress}
                    setBlacklistData={(data) => setBlacklistData(data)}
                    setBlacklistDataItem={(data) => setBlacklistDataItem(data)}
                    activePermissions={activePermissions}
                    hideApproveActions={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_APPROVE_ACTION,
                      )
                    }
                    hideRejectActions={
                      !activePermissions?.includes(
                        PermissionEnum.APPLICATIONS_CUSTOMERS_AML_REJECT_ACTION,
                      )
                    }
                    onResetTransactionWarningAction={(warningId) => {
                      if (onResetTransactionWarningAction) {
                        onResetTransactionWarningAction(warningId, item.id);
                      }
                    }}
                    resetTransactionsWarningIsLoading={
                      resetTransactionsWarningIsLoading
                    }
                    resetTransactionsWarningIsSuccess={
                      resetTransactionsWarningIsSuccess
                    }
                    warningInGroupId={selectedWarningId}
                    getWarningById={getOngoingWarningById}
                    entityId={item.id}
                    isWarningByIdFetching={isWarningByIdFetching}
                    isWarningByIdLoading={isWarningByIdLoading}
                    globalCollapsed
                    isPaymentOrder
                    withoutChat
                  />
                ))}
                {getApplicationWarningsAction &&
                  item?.warnings?.pageInfo.hasNextPage && (
                    <div className={cn.warningsGroupPagination}>
                      <ButtonPseudo
                        text={
                          selectedWarningGroup === item.id &&
                          paginationInProgress
                            ? 'Loading...'
                            : 'Show more'
                        }
                        disabled={paginationInProgress}
                        onClick={() =>
                          getApplicationWarningsAction({
                            warningGroupId: item.id,
                            cursor: item?.warnings?.pageInfo?.endCursor,
                          })
                        }
                        bold
                      />
                    </div>
                  )}
              </div>
            ))}
            {applyError && <div className={cn.applyError}>{applyError}</div>}
            {ongoingMonitoring &&
              ongoingMonitoring[0] &&
              ongoingMonitoring[0]?.warningsGroupsBlacklists?.edges?.length >
                0 && (
                <div className={cn.checkedBlockLists}>
                  <div className={cn.checkedBlacklistTitle}>
                    Checked blocklists
                  </div>
                  <ul className={cn.blacklists}>
                    {ongoingMonitoring[0]?.warningsGroupsBlacklists?.edges?.map(
                      (item) => (
                        <li key={item.node.id}>{item.node.blacklist.name}</li>
                      ),
                    )}
                  </ul>
                </div>
              )}
          </div>
        );
      } else if (
        !ongoingMonitoring?.length ||
        (ongoingMonitoring && !ongoingMonitoring[0]?.warnings?.edges?.length)
      ) {
        content = (
          <>
            <div className={cn.waitingStatus}>
              <IconSelected /> No matches found
            </div>

            {ongoingMonitoring &&
              ongoingMonitoring[0] &&
              ongoingMonitoring[0]?.warningsGroupsBlacklists?.edges?.length >
                0 && (
                <div className={cn.checkedBlockLists}>
                  <div className={cn.checkedBlacklistTitle}>
                    Checked blocklists
                  </div>
                  <ul className={cn.blacklists}>
                    {ongoingMonitoring[0]?.warningsGroupsBlacklists?.edges?.map(
                      (item) => (
                        <li key={item.node.id}>{item.node.blacklist.name}</li>
                      ),
                    )}
                  </ul>
                </div>
              )}
          </>
        );
      }
    }
  }

  return (
    <>
      <div className={cn.stepBlockWrapper} style={{ zIndex: 10 }}>
        <div className={cn.stepBlockContent}>
          <StepPartBlock title="Ongoing monitoring" zIndex={10}>
            {content}
          </StepPartBlock>
        </div>
      </div>
      <Popup
        title="Select status and action"
        description="This will be applied to all matches in the group"
        onCancelClick={() => {
          setApplyAllPopupVisible(false);
          setComment('');
          setMatchStatus('');
          setAction('');
        }}
        onOkClick={onApplyActions}
        cancelButtonText="Cancel"
        okButtonText={
          batchApplyWarningInProgress ? 'Loading...' : 'Apply to all'
        }
        okButtonDisabled={
          !comment || !action || !matchStatus || batchApplyWarningInProgress
        }
        isVisible={applyAllPopupVisible}
      >
        <div className={cn.applyAllContent}>
          <Select
            name="matchStatus"
            value={matchStatus}
            onChange={(e) => setMatchStatus(e.target.value as string)}
            list={[
              {
                name: 'False',
                value: MatchStatusEnum.FALSE,
              },
              {
                name: 'True',
                value: MatchStatusEnum.TRUE,
              },
              {
                name: 'Potential',
                value: MatchStatusEnum.POTENTIAL,
              },
            ]}
            placeholder="Select Match status"
            disabled={
              !ongoingWithoutActions.length || batchApplyWarningInProgress
            }
            className={cn.matchStatusSelect}
            fullWidth
          />
          <Select
            name="actions"
            value={action}
            onChange={(e) => setAction(e.target.value as string)}
            list={[
              {
                name: 'Pass all time for this client',
                value:
                  TransactionWarningActionEnum.PASS_ALL_TIME_FOR_THIS_CLIENT,
                hidden: !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_ACCEPT_ACTION,
                ),
              },
              {
                name: 'Pass combination',
                value: TransactionWarningActionEnum.PASS_COMBINATION,
                hidden: !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_ACCEPT_ACTION,
                ),
              },
              {
                name: 'Pass once',
                value: TransactionWarningActionEnum.PASS_ONCE,
                hidden: !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_ACCEPT_ACTION,
                ),
              },
              {
                name: 'Stop',
                value: TransactionWarningActionEnum.STOP,
                hidden: !activePermissions?.includes(
                  PermissionEnum.APPLICATIONS_CUSTOMERS_AML_REJECT_ACTION,
                ),
              },
            ]}
            placeholder="Select actions"
            className={cn.matchStatusSelect}
            disabled={
              !ongoingWithoutActions.length || batchApplyWarningInProgress
            }
            fullWidth
          />
          <Textarea
            name="comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Enter a comment..."
            disabled={
              !ongoingWithoutActions.length || batchApplyWarningInProgress
            }
          />
        </div>
      </Popup>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistDataVisible}
        onClose={() => {
          setBlacklistDataVisible(false);
          setBlacklistData(undefined);
        }}
        title={blacklistData?.fullName as string}
        sidepanelExpandedKey="customers-ongoing-monitoring-blacklist"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setBlacklistDataVisible(false);
              setBlacklistData(undefined);
            }}
            bold
          />
        }
      >
        <BlacklistDataContent data={blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={blacklistItemVisible}
        onClose={() => {
          setBlacklistDataItem(undefined);
          setBlacklistItemVisible(false);
        }}
        title="User Details"
        sidepanelExpandedKey="customers-ongoing-monitoring-blacklist"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setBlacklistDataItem(undefined);
              setBlacklistItemVisible(false);
            }}
            bold
          />
        }
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={blacklistItem} isLoading={false} />
      </SidePanel>
    </>
  );
};

export default StepFive;
